// =============================================================================
// iTravel 2.0 — Auth Store (Zustand)
// =============================================================================
// Manages authentication state including user data, tokens, and auth lifecycle.
// This store is used outside of React components too (e.g., API interceptors,
// sync engine), which is why Zustand is perfect — no Provider required.
// =============================================================================

import type { AuthTokens, User, UserSettings } from '@itravel/shared';
import { create } from 'zustand';
import { combine, devtools, persist } from 'zustand/middleware';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthState {
  /** Current authentication status */
  status: AuthStatus;

  /** Authenticated user data (null when not authenticated) */
  user: User | null;

  /** User settings / preferences */
  settings: UserSettings | null;

  /** JWT access token (short-lived, kept in memory for security) */
  accessToken: string | null;

  /** JWT refresh token (long-lived, persisted in secure storage) */
  refreshToken: string | null;

  /** Whether the initial auth check (token validation) has completed */
  isHydrated: boolean;

  /** Error from the last auth operation, if any */
  error: string | null;
}

// -----------------------------------------------------------------------------
// Initial State
// -----------------------------------------------------------------------------

const initialState: AuthState = {
  status: 'idle',
  user: null,
  settings: null,
  accessToken: null,
  refreshToken: null,
  isHydrated: false,
  error: null,
};

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------

export const useAuthStore = create(
  devtools(
    persist(
      combine(initialState, (set, get) => ({
        // -------------------------------------------------------------------
        // Actions
        // -------------------------------------------------------------------

        /**
         * Set auth status to loading (e.g., during login/register API calls).
         */
        setLoading: () => {
          set({ status: 'loading', error: null }, false, 'auth/setLoading');
        },

        /**
         * Set auth error and mark status as unauthenticated.
         */
        setError: (error: string) => {
          set({ status: 'unauthenticated', error }, false, 'auth/setError');
        },

        /**
         * Clear any auth errors.
         */
        clearError: () => {
          set({ error: null }, false, 'auth/clearError');
        },

        /**
         * Called after successful login or registration.
         * Stores user data and tokens.
         */
        setAuthenticated: (user: User, tokens: AuthTokens) => {
          set(
            {
              status: 'authenticated',
              user,
              accessToken: tokens.accessToken,
              refreshToken: tokens.refreshToken,
              error: null,
            },
            false,
            'auth/setAuthenticated',
          );
        },

        /**
         * Update the stored user data (e.g., after profile edit).
         */
        setUser: (user: User) => {
          set({ user }, false, 'auth/setUser');
        },

        /**
         * Partially update user fields without replacing the entire object.
         */
        updateUser: (updates: Partial<User>) => {
          const currentUser = get().user;
          if (!currentUser) return;
          set({ user: { ...currentUser, ...updates } }, false, 'auth/updateUser');
        },

        /**
         * Update user settings / preferences.
         */
        setSettings: (settings: UserSettings) => {
          set({ settings }, false, 'auth/setSettings');
        },

        /**
         * Update a subset of user settings.
         */
        updateSettings: (updates: Partial<UserSettings>) => {
          const currentSettings = get().settings;
          if (!currentSettings) return;
          set({ settings: { ...currentSettings, ...updates } }, false, 'auth/updateSettings');
        },

        /**
         * Update tokens after a refresh cycle.
         */
        setTokens: (tokens: AuthTokens) => {
          set(
            {
              accessToken: tokens.accessToken,
              refreshToken: tokens.refreshToken,
            },
            false,
            'auth/setTokens',
          );
        },

        /**
         * Update only the access token (after a silent refresh).
         */
        setAccessToken: (accessToken: string) => {
          set({ accessToken }, false, 'auth/setAccessToken');
        },

        /**
         * Mark the initial hydration check as complete.
         * Called after we attempt to validate any persisted tokens on app startup.
         */
        setHydrated: () => {
          set({ isHydrated: true }, false, 'auth/setHydrated');
        },

        /**
         * Log out — clear all auth state and return to initial state.
         */
        logout: () => {
          set(
            {
              ...initialState,
              isHydrated: true,
              status: 'unauthenticated',
            },
            false,
            'auth/logout',
          );
        },

        /**
         * Full reset — clear everything including hydration status.
         * Used during account deletion or forced logout.
         */
        reset: () => {
          set(initialState, false, 'auth/reset');
        },

        // -------------------------------------------------------------------
        // Computed / Derived (as getters via functions)
        // -------------------------------------------------------------------

        /**
         * Whether the user is currently authenticated.
         */
        isAuthenticated: (): boolean => {
          return get().status === 'authenticated' && get().user !== null;
        },

        /**
         * Get the current access token (used by API interceptors).
         * Returns null if not authenticated.
         */
        getAccessToken: (): string | null => {
          return get().accessToken;
        },

        /**
         * Get the current refresh token.
         */
        getRefreshToken: (): string | null => {
          return get().refreshToken;
        },

        /**
         * Get the current user's ID, or null.
         */
        getUserId: (): string | null => {
          return get().user?.id ?? null;
        },

        /**
         * Get the user's preferred language for AI guides and i18n.
         */
        getPreferredLanguage: (): string => {
          return get().settings?.preferredLanguage ?? get().user?.preferredLanguage ?? 'en';
        },

        /**
         * Get the user's preferred currency for expense tracking.
         */
        getPreferredCurrency: (): string => {
          return get().settings?.preferredCurrency ?? 'EUR';
        },
      })),
      {
        name: 'itravel-auth-storage',
        // Persist tokens and basic user info so sessions survive page reloads.
        // The access token IS persisted now — if it's still valid after a
        // refresh, we skip the 401→rotate dance entirely. If it has expired,
        // the API interceptor handles the refresh transparently.
        // NOT persisting the access token was causing unnecessary refresh-token
        // rotation on every page reload, which combined with a stale-closure
        // bug in hydration led to "token reuse detected" → full logout.
        partialize: (state) => ({
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          user: state.user,
          settings: state.settings,
        }),
      },
    ),
    { name: 'AuthStore', enabled: import.meta.env.DEV },
  ),
);

// -----------------------------------------------------------------------------
// Selectors (for optimized re-renders)
// -----------------------------------------------------------------------------
// Use these selectors with `useAuthStore(selectX)` to avoid unnecessary
// re-renders when unrelated parts of the store change.
// -----------------------------------------------------------------------------

export const selectAuthStatus = (state: AuthState) => state.status;
export const selectUser = (state: AuthState) => state.user;
export const selectSettings = (state: AuthState) => state.settings;
export const selectIsAuthenticated = (state: AuthState) =>
  state.status === 'authenticated' && state.user !== null;
export const selectIsHydrated = (state: AuthState) => state.isHydrated;
export const selectAuthError = (state: AuthState) => state.error;
export const selectIsLoading = (state: AuthState) => state.status === 'loading';
export const selectAccessToken = (state: AuthState) => state.accessToken;
